/**
 * Client-side Voronoi tessellation of a silhouette CONTOUR — the browser twin of
 * the worker's `_generate_voronoi_maze` (portal-workers/app/workers/maze.py),
 * minus the pixel mask: the polygon the worker already extracted stands in for it.
 *
 * Why: a saved photo maze from before per-level generation carries ONE geometry
 * (one cell count). Switching its difficulty used to mean a paid re-queue to the
 * worker. The silhouette is the expensive part and we already have it, so the
 * cells for another level are re-sampled right here: Poisson-disk seeds inside
 * the contour (same `sqrt(area / count) * 0.85` spacing as the worker), a
 * Delaunay/Voronoi over them (d3-delaunay), cells clipped to the contour's bbox
 * (like the worker — the contour mask + adjacency filter do the silhouette work
 * downstream, in recarveMaze / the renderer). The result is a fresh
 * VoronoiMazeData in the SAME coordinate space as the contour passed in, with
 * `allNeighbors` set, ready for recarveMaze (which picks entry/exit, carves the
 * spanning tree with the passage-width floor, and guarantees a solution path).
 */
import { Delaunay } from 'd3-delaunay';
import type { Position, VoronoiMazeData, VoronoiRegion } from '../types/maze-types';
import type { GameDifficulty } from '@/contexts/games/types';

/**
 * Seed points per difficulty — mirrors the worker's `_SEED_COUNTS`. These are
 * the FLOOR: the worker (and the template JSONs) tessellate for a phone-sized
 * canvas; on a desktop the same 350 expert cells are 40 px wide and the maze
 * is trivial. See `seedCountForContour`.
 */
export const VORONOI_SEED_COUNTS: Record<GameDifficulty, number> = {
  easy: 60,
  medium: 120,
  hard: 200,
  expert: 350,
};

/**
 * Target cell diameter on screen (canvas px) per level — the Voronoi twin of the
 * rect maze's `cellSize` (40/30/22/18 in mazeUtils), so "expert" is the same
 * corridor width whatever the shape. Cell count = silhouette area / cell².
 */
export const VORONOI_CELL_PX: Record<GameDifficulty, number> = {
  easy: 44,
  medium: 32,
  hard: 25,
  expert: 20,
};

/** Hard cap on cells (recarve + render stay instant; a 4K desktop would ask for more). */
export const VORONOI_MAX_SEEDS = 1600;

/**
 * How many cells `difficulty` needs for a contour ALREADY IN CANVAS px: enough
 * to make the cells `VORONOI_CELL_PX` wide, never fewer than the worker's count
 * (a phone canvas keeps exactly what the worker made), capped.
 */
export function seedCountForContour(contour: Position[], difficulty: GameDifficulty): number {
  const base = VORONOI_SEED_COUNTS[difficulty];
  if (!contour || contour.length < 3) return base;
  const wanted = Math.round(polygonArea(contour) / VORONOI_CELL_PX[difficulty] ** 2);
  return Math.min(VORONOI_MAX_SEEDS, Math.max(base, wanted));
}

/**
 * A worker / template geometry has a fixed cell count. When the screen fits
 * ≥ 25 % more cells at the level's target size (desktop, tablet), re-tessellate
 * the contour locally so the level is as hard as it is on a phone. Otherwise
 * the geometry is returned as is (same object). Call AFTER the fit — the
 * contour must be in canvas px.
 */
export function densifyForScreen(mazeData: VoronoiMazeData, difficulty: GameDifficulty): VoronoiMazeData {
  const contour = mazeData.contour;
  if (!contour || contour.length < 3) return mazeData;
  const want = seedCountForContour(contour, difficulty);
  if (want < mazeData.regions.length * 1.25) return mazeData;
  return (
    tessellateFromContour(contour, difficulty, {
      seedCount: want,
      aspectRatio: mazeData.aspectRatio,
      geometryTransform: mazeData.geometryTransform,
    }) ?? mazeData
  );
}

function pointInPolygon(px: number, py: number, poly: Position[]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i].x;
    const yi = poly[i].y;
    const xj = poly[j].x;
    const yj = poly[j].y;
    if (yi > py !== yj > py && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

function polygonArea(poly: Position[]): number {
  let a = 0;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    a += (poly[j].x + poly[i].x) * (poly[j].y - poly[i].y);
  }
  return Math.abs(a) / 2;
}

/** Deterministic-enough RNG hook so tests can pin a seed; default Math.random. */
export type Rng = () => number;

/**
 * Poisson-disk dart throwing inside `contour`: uniform-ish cells, no two seeds
 * closer than `minDist`. Grid-accelerated; gives up after a bounded number of
 * misses (a very thin shape may not fit `count` seeds — fewer cells is fine).
 */
export function samplePoissonInContour(contour: Position[], count: number, rng: Rng = Math.random): Position[] {
  const xs = contour.map((p) => p.x);
  const ys = contour.map((p) => p.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const area = polygonArea(contour);
  if (!(area > 0) || !(count > 0)) return [];
  const minDist = Math.sqrt(area / count) * 0.85;
  const cell = minDist / Math.SQRT2;
  const grid = new Map<string, number>();
  const points: Position[] = [];
  const key = (x: number, y: number) => `${Math.floor(x / cell)},${Math.floor(y / cell)}`;
  let misses = 0;
  const maxMisses = count * 60;
  while (points.length < count && misses < maxMisses) {
    const x = minX + rng() * (maxX - minX);
    const y = minY + rng() * (maxY - minY);
    if (!pointInPolygon(x, y, contour)) {
      misses++;
      continue;
    }
    const gx = Math.floor(x / cell);
    const gy = Math.floor(y / cell);
    let tooClose = false;
    for (let dx = -2; dx <= 2 && !tooClose; dx++) {
      for (let dy = -2; dy <= 2; dy++) {
        const idx = grid.get(`${gx + dx},${gy + dy}`);
        if (idx === undefined) continue;
        const p = points[idx];
        if ((p.x - x) ** 2 + (p.y - y) ** 2 < minDist * minDist) {
          tooClose = true;
          break;
        }
      }
    }
    if (tooClose) {
      misses++;
      continue;
    }
    grid.set(key(x, y), points.length);
    points.push({ x, y });
  }
  return points;
}

export interface TessellateOptions {
  /** Override the per-difficulty seed count. */
  seedCount?: number;
  rng?: Rng;
  /** Carried into the result (canvas sizing); defaults to the contour's bbox ratio. */
  aspectRatio?: number;
  /** Carried into the result (photo placement) — the contour is already in canvas space. */
  geometryTransform?: VoronoiMazeData['geometryTransform'];
}

/**
 * Tessellate `contour` (already in the target coordinate space) for `difficulty`.
 * Returns null when the contour is degenerate (fewer than 4 seeds fit) — the
 * caller then falls back to the worker.
 */
export function tessellateFromContour(
  contour: Position[],
  difficulty: GameDifficulty,
  opts: TessellateOptions = {},
): VoronoiMazeData | null {
  if (!contour || contour.length < 3) return null;
  const count = opts.seedCount ?? seedCountForContour(contour, difficulty);
  const seeds = samplePoissonInContour(contour, count, opts.rng ?? Math.random);
  if (seeds.length < 4) return null;

  const xs = contour.map((p) => p.x);
  const ys = contour.map((p) => p.y);
  const pad = 2;
  const bounds: [number, number, number, number] = [
    Math.min(...xs) - pad,
    Math.min(...ys) - pad,
    Math.max(...xs) + pad,
    Math.max(...ys) + pad,
  ];

  const delaunay = Delaunay.from(seeds, (p) => p.x, (p) => p.y);
  const voronoi = delaunay.voronoi(bounds);

  const regions: VoronoiRegion[] = [];
  const idOf = new Map<number, number>(); // seed index → region id
  for (let i = 0; i < seeds.length; i++) {
    const poly = voronoi.cellPolygon(i);
    if (!poly || poly.length < 4) continue; // closed ring: first == last
    const verts: Position[] = [];
    for (let k = 0; k < poly.length - 1; k++) {
      const v = { x: Math.round(poly[k][0] * 10) / 10, y: Math.round(poly[k][1] * 10) / 10 };
      const prev = verts[verts.length - 1];
      if (prev && prev.x === v.x && prev.y === v.y) continue;
      verts.push(v);
    }
    if (verts.length < 3) continue;
    idOf.set(i, regions.length);
    regions.push({
      id: regions.length,
      vertices: verts,
      site: { x: Math.round(seeds[i].x * 10) / 10, y: Math.round(seeds[i].y * 10) / 10 },
      neighbors: [],
      allNeighbors: [],
    });
  }
  if (regions.length < 4) return null;

  for (const [seedIdx, rid] of idOf) {
    const all: number[] = [];
    for (const n of delaunay.neighbors(seedIdx)) {
      const nid = idOf.get(n);
      if (nid !== undefined) all.push(nid);
    }
    regions[rid].allNeighbors = all.sort((a, b) => a - b);
  }

  const w = bounds[2] - bounds[0];
  const h = bounds[3] - bounds[1];
  return {
    regions,
    entryRegionId: regions[0].id,
    exitRegionId: regions[regions.length - 1].id,
    contour: contour.map((p) => ({ ...p })),
    aspectRatio: opts.aspectRatio ?? (h > 0 ? Math.round((w / h) * 1e4) / 1e4 : 1),
    geometryTransform: opts.geometryTransform ? { ...opts.geometryTransform } : undefined,
  };
}
